import React, { useState, useEffect, useCallback } from 'react';
import { adminAPI } from '../utils/api';
import './ConversationSummaries.css';

function ConversationSummaries() {
  // State for stats
  const [stats, setStats] = useState(null);

  // State for users dropdown (lazy loaded)
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserDropdown, setShowUserDropdown] = useState(false);

  // State for conversations list (lazy loaded per user)
  const [conversations, setConversations] = useState([]);
  const [conversationsLoading, setConversationsLoading] = useState(false);
  const [conversationSearch, setConversationSearch] = useState('');
  const [selectedConversation, setSelectedConversation] = useState(null);

  // State for summary detail (lazy loaded per conversation)
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);

  // Error state
  const [error, setError] = useState(null);

  // Load stats on mount
  useEffect(() => {
    loadStats();
    loadUsers();
  }, []);

  // Load stats
  const loadStats = async () => {
    try {
      const data = await adminAPI.getConversationStats();
      setStats(data);
    } catch (err) {
      console.error('Failed to load stats:', err);
    }
  };

  // Load users with debounced search
  const loadUsers = useCallback(async (search = '') => {
    setUsersLoading(true);
    try {
      const data = await adminAPI.getUsersWithConversations({ search, limit: 100 });
      setUsers(data.items || []);
    } catch (err) {
      console.error('Failed to load users:', err);
      setError('Failed to load users');
    } finally {
      setUsersLoading(false);
    }
  }, []);

  // Debounce user search
  useEffect(() => {
    const timer = setTimeout(() => {
      loadUsers(userSearch);
    }, 300);
    return () => clearTimeout(timer);
  }, [userSearch, loadUsers]);

  // Load conversations when user is selected
  useEffect(() => {
    if (selectedUser) {
      loadConversations(selectedUser.user_id);
    } else {
      setConversations([]);
      setSelectedConversation(null);
      setSummary(null);
    }
  }, [selectedUser]);

  // Load conversations for user
  const loadConversations = async (userId, search = '') => {
    setConversationsLoading(true);
    setSelectedConversation(null);
    setSummary(null);
    try {
      const data = await adminAPI.getUserConversations(userId, { search, limit: 50 });
      setConversations(data.items || []);
    } catch (err) {
      console.error('Failed to load conversations:', err);
      setError('Failed to load conversations');
    } finally {
      setConversationsLoading(false);
    }
  };

  // Debounce conversation search
  useEffect(() => {
    if (selectedUser) {
      const timer = setTimeout(() => {
        loadConversations(selectedUser.user_id, conversationSearch);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [conversationSearch, selectedUser]);

  // Load summary when conversation is selected
  const handleSelectConversation = async (conversation) => {
    setSelectedConversation(conversation);
    setSummaryLoading(true);
    setSummary(null);

    try {
      if (conversation.has_summary) {
        const data = await adminAPI.getConversationSummary(conversation.conversation_id);
        setSummary(data);
      } else {
        setSummary({ no_summary: true });
      }
    } catch (err) {
      console.error('Failed to load summary:', err);
      setSummary({ error: true, message: err.response?.data?.detail || 'Failed to load summary' });
    } finally {
      setSummaryLoading(false);
    }
  };

  // Handle user selection
  const handleSelectUser = (user) => {
    setSelectedUser(user);
    setShowUserDropdown(false);
    setConversationSearch('');
  };

  // Clear selection
  const handleClearUser = () => {
    setSelectedUser(null);
    setConversations([]);
    setSelectedConversation(null);
    setSummary(null);
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="conversation-summaries">
      <div className="page-header">
        <div className="header-content">
          <h1>Conversation Summaries</h1>
          <p className="page-description">View conversation history and extracted summaries</p>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon">
              <ConversationIcon className="icon" />
            </div>
            <div className="stat-info">
              <div className="stat-value">{stats.total_conversations}</div>
              <div className="stat-label">Total Conversations</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">
              <SummaryIcon className="icon" />
            </div>
            <div className="stat-info">
              <div className="stat-value">{stats.total_summaries}</div>
              <div className="stat-label">Summaries Generated</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">
              <UsersIcon className="icon" />
            </div>
            <div className="stat-info">
              <div className="stat-value">{stats.total_users_with_conversations}</div>
              <div className="stat-label">Users with Conversations</div>
            </div>
          </div>
          <div className="stat-card active">
            <div className="stat-icon">
              <PercentIcon className="icon" />
            </div>
            <div className="stat-info">
              <div className="stat-value">{stats.coverage_percent}%</div>
              <div className="stat-label">Coverage</div>
            </div>
          </div>
        </div>
      )}

      {/* Error Banner */}
      {error && (
        <div className="error-banner">
          <ErrorIcon className="error-icon" />
          <span>{error}</span>
          <button onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}

      {/* Main Content */}
      <div className="content-layout">
        {/* Left Panel - User & Conversation Selection */}
        <div className="selection-panel">
          {/* User Selection */}
          <div className="selection-section">
            <h2>Select User</h2>
            <div className="user-selector">
              {selectedUser ? (
                <div className="selected-user">
                  <div className="user-info">
                    <div className="user-avatar">{selectedUser.email[0].toUpperCase()}</div>
                    <div className="user-details">
                      <div className="user-email">{selectedUser.email}</div>
                      <div className="user-count">{selectedUser.conversation_count} conversations</div>
                    </div>
                  </div>
                  <button className="clear-btn" onClick={handleClearUser}>
                    <ClearIcon className="icon" />
                  </button>
                </div>
              ) : (
                <div className="dropdown-container">
                  <input
                    type="text"
                    className="search-input"
                    placeholder="Search users by email or name..."
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    onFocus={() => setShowUserDropdown(true)}
                  />
                  {showUserDropdown && (
                    <div className="dropdown-list">
                      {usersLoading ? (
                        <div className="dropdown-loading">Loading users...</div>
                      ) : users.length === 0 ? (
                        <div className="dropdown-empty">No users found</div>
                      ) : (
                        users.map((user) => (
                          <div
                            key={user.user_id}
                            className="dropdown-item"
                            onClick={() => handleSelectUser(user)}
                          >
                            <div className="user-avatar small">{user.email[0].toUpperCase()}</div>
                            <div className="user-details">
                              <div className="user-email">{user.email}</div>
                              <div className="user-meta">
                                {user.conversation_count} conversations
                                {user.last_conversation_at && (
                                  <span> · Last: {formatDate(user.last_conversation_at)}</span>
                                )}
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Conversation Selection */}
          {selectedUser && (
            <div className="selection-section">
              <h2>Conversations</h2>
              <input
                type="text"
                className="search-input"
                placeholder="Filter by ID or title..."
                value={conversationSearch}
                onChange={(e) => setConversationSearch(e.target.value)}
              />
              <div className="conversation-list">
                {conversationsLoading ? (
                  <div className="list-loading">Loading conversations...</div>
                ) : conversations.length === 0 ? (
                  <div className="list-empty">No conversations found</div>
                ) : (
                  conversations.map((conv) => (
                    <div
                      key={conv.conversation_id}
                      className={`conversation-item ${selectedConversation?.conversation_id === conv.conversation_id ? 'selected' : ''}`}
                      onClick={() => handleSelectConversation(conv)}
                    >
                      <div className="conv-header">
                        <div className="conv-title">{conv.title || 'Untitled'}</div>
                        <div className={`summary-badge ${conv.has_summary ? 'has-summary' : 'no-summary'}`}>
                          {conv.has_summary ? 'Summary' : 'No Summary'}
                        </div>
                      </div>
                      <div className="conv-meta">
                        <span className="conv-id">{conv.conversation_id.slice(0, 8)}...</span>
                        <span className="conv-stats">
                          {conv.user_turn_count} turns · {conv.mode}
                        </span>
                      </div>
                      <div className="conv-date">{formatDate(conv.created_at)}</div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        {/* Right Panel - Summary Display */}
        <div className="summary-panel">
          {!selectedConversation ? (
            <div className="summary-empty">
              <SummaryIcon className="empty-icon" />
              <h3>Select a conversation</h3>
              <p>Choose a user and conversation to view the extracted summary</p>
            </div>
          ) : summaryLoading ? (
            <div className="summary-loading">
              <div className="spinner"></div>
              <span>Loading summary...</span>
            </div>
          ) : summary?.no_summary ? (
            <div className="summary-none">
              <NoSummaryIcon className="empty-icon" />
              <h3>No Summary Available</h3>
              <p>This conversation doesn't have a generated summary yet.</p>
              <p className="hint">Summaries are generated after {'>'}= 3 user turns.</p>
            </div>
          ) : summary?.error ? (
            <div className="summary-error">
              <ErrorIcon className="error-icon" />
              <h3>Failed to Load Summary</h3>
              <p>{summary.message}</p>
            </div>
          ) : summary ? (
            <div className="summary-content">
              <div className="summary-header">
                <h2>{selectedConversation.title || 'Untitled Conversation'}</h2>
                <div className="summary-meta">
                  <span>Turn {summary.turn_count}</span>
                  <span>·</span>
                  <span>Updated {formatDate(summary.last_updated)}</span>
                </div>
              </div>

              {summary.topic && (
                <div className="summary-section topic-section">
                  <h3>Topic</h3>
                  <div className="topic-content">{summary.topic}</div>
                </div>
              )}

              {summary.decisions_made?.length > 0 && (
                <div className="summary-section">
                  <h3>
                    <DecisionIcon className="section-icon" />
                    Decisions Made
                  </h3>
                  <ul className="summary-list">
                    {summary.decisions_made.map((decision, idx) => (
                      <li key={idx}>{decision}</li>
                    ))}
                  </ul>
                </div>
              )}

              {summary.user_preferences?.length > 0 && (
                <div className="summary-section">
                  <h3>
                    <PreferenceIcon className="section-icon" />
                    User Preferences
                  </h3>
                  <ul className="summary-list">
                    {summary.user_preferences.map((pref, idx) => (
                      <li key={idx}>{pref}</li>
                    ))}
                  </ul>
                </div>
              )}

              {summary.key_facts?.length > 0 && (
                <div className="summary-section">
                  <h3>
                    <FactIcon className="section-icon" />
                    Key Facts
                  </h3>
                  <ul className="summary-list">
                    {summary.key_facts.map((fact, idx) => (
                      <li key={idx}>{fact}</li>
                    ))}
                  </ul>
                </div>
              )}

              {summary.open_questions?.length > 0 && (
                <div className="summary-section">
                  <h3>
                    <QuestionIcon className="section-icon" />
                    Open Questions
                  </h3>
                  <ul className="summary-list questions">
                    {summary.open_questions.map((q, idx) => (
                      <li key={idx}>{q}</li>
                    ))}
                  </ul>
                </div>
              )}

              {summary.technologies_mentioned?.length > 0 && (
                <div className="summary-section">
                  <h3>
                    <TechIcon className="section-icon" />
                    Technologies Mentioned
                  </h3>
                  <div className="tags-list">
                    {summary.technologies_mentioned.map((tech, idx) => (
                      <span key={idx} className="tag tech-tag">{tech}</span>
                    ))}
                  </div>
                </div>
              )}

              {summary.commands_mentioned?.length > 0 && (
                <div className="summary-section">
                  <h3>
                    <CommandIcon className="section-icon" />
                    Commands
                  </h3>
                  <div className="commands-list">
                    {summary.commands_mentioned.map((cmd, idx) => (
                      <code key={idx} className="command">{cmd}</code>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </div>
      </div>

      {/* Click outside handler for dropdown */}
      {showUserDropdown && (
        <div
          className="dropdown-backdrop"
          onClick={() => setShowUserDropdown(false)}
        />
      )}
    </div>
  );
}

// Icons
function ConversationIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  );
}

function SummaryIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14,2 14,8 20,8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
      <line x1="10" y1="9" x2="8" y2="9" />
    </svg>
  );
}

function UsersIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}

function PercentIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="19" y1="5" x2="5" y2="19" />
      <circle cx="6.5" cy="6.5" r="2.5" />
      <circle cx="17.5" cy="17.5" r="2.5" />
    </svg>
  );
}

function ErrorIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  );
}

function ClearIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function NoSummaryIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14,2 14,8 20,8" />
      <line x1="9" y1="15" x2="15" y2="15" />
    </svg>
  );
}

function DecisionIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="9,11 12,14 22,4" />
      <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
    </svg>
  );
}

function PreferenceIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
    </svg>
  );
}

function FactIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="16" x2="12" y2="12" />
      <line x1="12" y1="8" x2="12.01" y2="8" />
    </svg>
  );
}

function QuestionIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10" />
      <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}

function TechIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="16,18 22,12 16,6" />
      <polyline points="8,6 2,12 8,18" />
    </svg>
  );
}

function CommandIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="4,17 10,11 4,5" />
      <line x1="12" y1="19" x2="20" y2="19" />
    </svg>
  );
}

export default ConversationSummaries;
